import { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../../../Helper/Api/axios.instance';
import { AppConfig } from '../../../Config/app-config';
import { useUserContext } from '../../../context/user.context';
import ethLogo from '../../../Assets/images/Icons/Ethreum.svg';
import usdcLogo from '../../../Assets/images/Icons/USDC.svg';
import usdtLogo from '../../../Assets/images/Icons/USDT.svg';
import DaiLogo from '../../../Assets/images/Icons/DAILogo.png';
import ozUSDLogo from '../../../Assets/images/Icons/ozUSD.svg';
import ozETH from '../../../Assets/images/Icons/ozETH.svg';
import DAIToken from '../../../ContractABI/DAIToken.json';

type TokenType = {
  abi: [];
  bridge: { abi: []; address: string; id: number };
  l1: { address: string; logo: number; name: string; decimals: number };
  l2: { address: string; logo: number; name: string; decimals: number };
  commission: number;
};

const DEFAULT_TOKEN_LIST: TokenType[] = [
  {
    abi: DAIToken as [],
    bridge: { abi: [], address: '', id: 0 },
    l1: { address: '', logo: ethLogo, name: 'ETH', decimals: 18 },
    l2: { address: '', logo: ozETH, name: 'ozETH', decimals: 18 },
    commission: 1,
  },
  {
    abi: DAIToken as [],
    bridge: { abi: [], address: '', id: 0 },
    l1: { address: '', logo: usdcLogo, name: 'USDC', decimals: 18 },
    l2: { address: '', logo: ozUSDLogo, name: 'ozUSD', decimals: 18 },
    commission: 1,
  },
  {
    abi: DAIToken as [],
    bridge: { abi: [], address: '', id: 0 },
    l1: { address: '', logo: usdtLogo, name: 'USDT', decimals: 18 },
    l2: { address: '', logo: ozUSDLogo, name: 'ozUSD', decimals: 18 },
    commission: 1,
  },
  {
    abi: DAIToken as [],
    bridge: { abi: [], address: '', id: 0 },
    l1: { address: '', logo: DaiLogo, name: 'DAI', decimals: 18 },
    l2: { address: '', logo: ozUSDLogo, name: 'ozUSD', decimals: 18 },
    commission: 1,
  },
];

export const useToken = () => {
  const { isLoggedIn } = useUserContext();
  const [tokenList, setTokenList] = useState<TokenType[]>(DEFAULT_TOKEN_LIST);

  const normalizeAbi = (abi: any): [] => {
    if (!Array.isArray(abi)) return DAIToken as [];
    const flattened = (abi as unknown[]).flat(2) as [];
    return flattened.length === 0 ? (DAIToken as []) : flattened;
  };

  const fetchTokenList = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(AppConfig.apiRoutes.listToken());
      const tokensWithFallbackAbi = data?.map((token: TokenType) => {
        const flattenedAbi = normalizeAbi(token?.abi);
        const hasMethod = (methodName: string) =>
          flattenedAbi.some((item: any) => item?.name === methodName);

        const shouldUseFallbackAbi =
          flattenedAbi.length === 0 ||
          !hasMethod('approve') ||
          !hasMethod('allowance') ||
          !hasMethod('balanceOf') ||
          !hasMethod('decimals');

        return {
          ...token,
          abi: shouldUseFallbackAbi ? (DAIToken as []) : flattenedAbi,
          commission: token?.commission ?? 1,
          l1: {
            ...token.l1,
            decimals: token?.l1?.decimals ?? 18,
          },
          l2: {
            ...token.l2,
            decimals: token?.l2?.decimals ?? 18,
          },
        };
      });

      setTokenList(tokensWithFallbackAbi);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      void fetchTokenList();
    } else {
      setTokenList(DEFAULT_TOKEN_LIST);
    }
  }, [isLoggedIn]);

  return {
    tokenList,
    fetchTokenList,
  };
};
